import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { modifyFiles } from "@utils/helpers";
import {IMailingAddress} from "@utils/interfaces"
import axios from "axios";
import { findKey } from "lodash";

export enum ConsentTypes {
  PARENT = "Parent",
  GUARDIAN = "Legal Guardian",
  NEITHER = "No Parent/Legal Guardian",
}

export enum uploadStatus {
  UPLOADING,
  SUCCESS,
  FAILED,
  QUEUE,
  INACTIVE,
}

export interface FileProgress {
  id: number;
  file: any;
  progress: number;
  cancelSource: any;
  status: number;
}
export interface InfoProps {
  salutation?: string | undefined | null;
  firstName?: string | undefined | null;
  lastName?: string | undefined | null;
  fullName: string | undefined | null;
  mobileNum?: string | undefined | null;
  emailId?: string | undefined | null;
  nric: string;
  dateOfBirth: string;
  nationality: string;
  residentialStatus: string;
  gender: string;
  mailingAddress?: IMailingAddress | null;
  stayInTouch?: string | undefined | null;
  colorFlag?: string | undefined | null;
  address: {
    block: string;
    unitNo: string;
    street: string;
    postal: string;
    building: string;
  };
  nationalityCode: string;
  customerId: number | undefined | null;
  age?: number;
}

export interface ErrorProps {
  errorCode: string;
  errorMessage: string;
}

export interface SignatureProps {
  attachmentId: string | null;
  documentType: string;
  fileType: string;
  base64SignatureImage: string;
}

export interface consentForMinorProps {
  type: string;
  fullName: string;
  consentAccepted: number;
}

export interface ChildDataListProps {
  childDataList: InfoProps[];
}
export interface PersonalChildInfoProps {
  availToPurch: ChildDataListProps | null;
  purchThroMyself: ChildDataListProps | null;
  existingShieldPlan: ChildDataListProps | null;
  doesNotQualifProd: ChildDataListProps | null;
  policyAppInProcess: ChildDataListProps | null;
  myselfData: InfoProps;
  error: ErrorProps;
}

export interface Step3State {
  personalInfo: InfoProps | { [key: string]: any | null };
  isEligible: boolean;
  personalChildInfo: PersonalChildInfoProps | {};
  selectedChildInfo: InfoProps | {};
  isManualMyinfoData: boolean;
  readyToGoNext: boolean;
  transactionId: string | null;
  openChildItem: string | null;
  rentalPostalCustomer: string | null;
  consentForMinor: consentForMinorProps;
  signature: SignatureProps;
  fileProgress: {};
  isSingpassInitialted: boolean
}

const initialState: Step3State = {
  personalInfo: {},
  isEligible: false,
  personalChildInfo: {},
  selectedChildInfo: {},
  isManualMyinfoData: false,
  readyToGoNext: false,
  transactionId: null,
  openChildItem: null,
  rentalPostalCustomer: "",
  consentForMinor: {
    type: "",
    fullName: "",
    consentAccepted: 0,
  },
  signature: {
    attachmentId: null,
    documentType: "",
    fileType: "",
    base64SignatureImage: "",
  },
  fileProgress: {},
  isSingpassInitialted: false
};

const step3Slice = createSlice({
  name: "SHIELD_STARTER-Step3",
  initialState,
  reducers: {
    updatePersonalInfo: (
      state,
      action: PayloadAction<InfoProps | { [key: string]: any } | null>
    ) => {
      state.personalInfo = { ...action.payload };
    },
    updateSingpassInitiated: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isSingpassInitialted = action.payload;
    },
    updateMailingAddressState: (
      state,
      action: PayloadAction<IMailingAddress | null>
    ) => {
      state.personalInfo = {
        ...state.personalInfo,
        mailingAddress: action.payload,
      };
    },
    updateEligible: (state, action: PayloadAction<any>) => {
      state.isEligible = action.payload;
    },
    updatePersonalChildInfo: (
      state,
      action: PayloadAction<PersonalChildInfoProps>
    ) => {
      state.personalChildInfo = { ...action.payload };
    },
    updateSelectedChildInfo: (state, action: PayloadAction<InfoProps | {}>) => {
      state.selectedChildInfo = action.payload;
    },
    updateIsManualMyinfoData: (state, action: PayloadAction<boolean>) => {
      state.isManualMyinfoData = action.payload;
    },
    updateReadyToGoNext: (state, action: PayloadAction<boolean>) => {
      state.readyToGoNext = action.payload;
    },
    updateTransactionId: (state, action: PayloadAction<string>) => {
      state.transactionId = action.payload;
    },
    updateOpenChildItem: (state, action: PayloadAction<string | null>) => {
      state.openChildItem = action.payload;
    },
    updateChildItem: (state, action: PayloadAction<any>) => {
      const payload = action.payload;
      const personalChildInfo: any = state.personalChildInfo;
      personalChildInfo[payload.itemType].childDataList[payload.index] =
        payload.item;
      state.personalChildInfo = personalChildInfo;
    },
    updateRentalPostalCustomer: (state, action: PayloadAction<string>) => {
      state.rentalPostalCustomer = action.payload;
    },
    updateConsentForMinor: (
      state,
      action: PayloadAction<consentForMinorProps>
    ) => {
      state.consentForMinor = action.payload;
    },
    updateSignature: (state, action: PayloadAction<any>) => {
      state.signature = action.payload;
    },
    deleteSignature: (state) => {
      state.signature = initialState.signature;
    },
    updateToDefaultStateStep3: (state, action: PayloadAction<string>) => ({
      ...initialState,
    }),
    setUploadFile: (state, action: PayloadAction<any>) => {
      state.fileProgress = {
        ...state.fileProgress,
        ...modifyFiles(state.fileProgress, action.payload),
      };
    },
    clearUploadedFiles: (state) => {
      state.fileProgress = {};
    },
    deleteUploadedFile: (state, action: PayloadAction<any>) => {
      delete state.fileProgress[
        action.payload as keyof typeof state.fileProgress
      ];
    },
    setUploadProgress: (state, action: PayloadAction<any>) => {
      const fileProgress: any =
        state.fileProgress[
          action.payload.id as keyof typeof state.fileProgress
        ];
      state.fileProgress = {
        ...state.fileProgress,
        [action.payload.id]: {
          ...fileProgress,
          status: uploadStatus.UPLOADING,
          progress: action.payload.progress,
        },
      };
    },
    successUploadFile: (state, action: PayloadAction<any>) => {
      const fileProgress: any =
        state.fileProgress[
          action.payload.id as keyof typeof state.fileProgress
        ];

      const nextFileKey: string | undefined = findKey(
        state.fileProgress,
        function (file: any) {
          return file?.status === uploadStatus.INACTIVE;
        }
      );

      const updatedFiles: any = {
        ...state.fileProgress,
        [action.payload.id]: {
          ...fileProgress,
          status: uploadStatus.SUCCESS,
          attachmentId: action.payload.attachmentId,
        },
      };

      if (nextFileKey) {
        const nextFile: any =
          state.fileProgress[nextFileKey as keyof typeof state.fileProgress];

        updatedFiles[nextFileKey] = {
          ...nextFile,
          status: uploadStatus.QUEUE,
        };
      }

      state.fileProgress = updatedFiles;
    },
    failureUploadFiles: (state, action: PayloadAction<any>) => {
      const fileProgress: any =
        state.fileProgress[
          action.payload.id as keyof typeof state.fileProgress
        ];

      const nextFileKey: string | undefined = findKey(
        state.fileProgress,
        function (file: any) {
          return file?.status === uploadStatus.INACTIVE;
        }
      );

      const updatedFiles: any = {
        ...state.fileProgress,
        [action.payload.id]: {
          ...fileProgress,
          status: uploadStatus.FAILED,
          progress: 0,
          error: action.payload.error,
        },
      };

      if (nextFileKey) {
        const nextFile: any =
          state.fileProgress[nextFileKey as keyof typeof state.fileProgress];

        updatedFiles[nextFileKey] = {
          ...nextFile,
          status: uploadStatus.QUEUE,
        };
      }

      state.fileProgress = updatedFiles;
    },
    retryUploadFiles: (state, action: PayloadAction<any>) => {
      const CancelToken = axios.CancelToken;
      const cancelSource = CancelToken.source();

      const fileProgress: any =
        state.fileProgress[action.payload as keyof typeof state.fileProgress];

      state.fileProgress = {
        ...state.fileProgress,
        [action.payload]: {
          ...fileProgress,
          status: uploadStatus.UPLOADING,
          progress: 0,
          cancelSource,
        },
      };
    },
  },
  extraReducers: (builder) => {},
});

export const {
  updatePersonalInfo,
  updateSingpassInitiated,
  updateMailingAddressState,
  updateEligible,
  updatePersonalChildInfo,
  updateSelectedChildInfo,
  updateReadyToGoNext,
  updateIsManualMyinfoData,
  updateTransactionId,
  updateOpenChildItem,
  updateChildItem,
  updateRentalPostalCustomer,
  updateConsentForMinor,
  updateToDefaultStateStep3,
  updateSignature,
  setUploadFile,
  deleteUploadedFile,
  setUploadProgress,
  successUploadFile,
  failureUploadFiles,
  retryUploadFiles,
  clearUploadedFiles,
  deleteSignature,
} = step3Slice.actions;

export default step3Slice.reducer;
